import { Component, OnInit, Type } from '@angular/core';

import { Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AccessService, CourseService, IntroService, ResponsiveService, ScreenSize, SubjectService } from '../core';
import { Content, Course, SchoolFeatures, Subject, SubjectType } from '../core/models';
import { Auth } from '../root-store';
import { LanguagePipe } from '../shared/language/language.pipe';

import { DynamicWidgetLoaderService } from '../dashboard-widgets/services';

@Component({
    templateUrl: 'dashboard.component.html',
    styleUrls: ['dashboard.component.css'],
    providers: [LanguagePipe]
})
export class DashboardComponent implements OnInit {
    public canAccessStore: boolean;
    public canAccessThinkDaily$: Observable<boolean>;
    public courseSlideOptions = [1, 3, 4, 6, 8];
    public loadingNextUp = true;
    public nextUp: { content: Content; contentList: Content[]; course: Course }[] = [];
    public ownedCourses: Course[] = [];
    public ownedOrder = 'Recently Added';
    public ownedOrderOptions = ['Recently Added', 'Recently Watched', 'Subject (A-Z)', 'Title (A-Z)'];
    public premiumCourses: Course[] = [];
    public premiumOrder = 'Title (A-Z)';
    public premiumOrderOptions = ['Subject (A-Z)', 'Title (A-Z)'];
    public screenSize: ScreenSize;
    public selectedTabIndex = 0;
    public subjects: Subject[];
    public subjectTypes: SubjectType[];
    public tabTitles = [
        { title: 'Bulletin' },
        { title: 'Up Next' },
        { title: 'My Courses', transform: () => this.languagePipe.transform('COURSES').pipe(map((value: string) => `My ${value}`)) },
        { title: 'Tools', conditional: () => of(true) }, // Conditional is set to of(false) in ngOnInit
        { title: 'Think Daily', conditional: () => this.canAccessThinkDaily$ },
        { title: 'Premium Courses', conditional: () => of(this.canAccessStore), transform: () => this.languagePipe.transform('COURSES').pipe(map((value: string) => `Premium ${value}`)) }
    ];
    public userToken: string;
    public widgetComponents: { component: Type<any>; id: number }[] = [];

    constructor(
        private intro: IntroService,
        private courseService: CourseService,
        private responsiveService: ResponsiveService,
        private subjectService: SubjectService,
        private widgetLoaderService: DynamicWidgetLoaderService,
        private accessService: AccessService,
        private store: Store<Auth.State>,
        private languagePipe: LanguagePipe
    ) {
        this.canAccessThinkDaily$ = this.store.select(Auth.selectSchoolFeatures).pipe(
            map((features: SchoolFeatures) => features.use_think_daily)
        );
    }

    public changeTabTo(toTabIndex: number): void {
        if (this.selectedTabIndex !== toTabIndex) {
            this.selectedTabIndex = toTabIndex;
            const sections: HTMLElement[] = Array.from(document.querySelectorAll('.section'));
            if (sections[toTabIndex]) {
                sections[toTabIndex].scrollIntoView(true);
                window.scroll(0, window.scrollY - 40); // Offset for fixed tab header
            }
        }
    }

    public hideHints(): void {
        this.intro.hideHints();
    }

    public ngOnInit(): void {
        this.responsiveService.screenSize.subscribe((size: ScreenSize) => {
            this.screenSize = size;
        });

        forkJoin([
            this.courseService.getOwned(),
            this.courseService.getUnowned()
        ]).subscribe(([ownedCourses, unownedCourses]: [Course[], Course[]]) => {
            this.ownedCourses = ownedCourses.sort((a: Course, b: Course) => {
                if (a.wrapped !== b.wrapped) {
                    return a.wrapped ? -1 : 1;
                } else if (a.created_on && b.created_on) {
                    return a.created_on > b.created_on ? -1 : 1;
                }

                return 0;
            });

            this.premiumCourses = unownedCourses;
            this.refreshNextUp();
        });

        this.widgetLoaderService.getComponents()
            .subscribe((components: { component: Type<any>; id: number }[]) => {
                this.widgetComponents = components.slice(0, 4);

                if (this.widgetComponents.length === 0) {
                    const tab = this.tabTitles.find((t: any) => t.title === 'Tools');
                    if (tab) {
                        tab.conditional = () => of(false);
                    }
                }
            });

        this.accessService.canAccessStore()
            .subscribe(
                (canAccessStore: boolean) => {
                    this.canAccessStore = canAccessStore;
                },
                (err: any) => {
                    this.canAccessStore = false;
                }
            );

        this.subjectService.getOwned()
            .subscribe(
                (subjects: Subject[]) => {
                    this.subjects = subjects;
                },
                (err: any) => {
                    this.subjects = [];
                }
            );

        this.subjectService.getTypes()
            .subscribe(
                (types: SubjectType[]) => {
                    // Make sure there's at least one subject type ("Subjects" by default)
                    if (types.length === 0) {
                        types.push({ id: 0, title: 'Subjects' });
                    }
                    this.subjectTypes = types;
                },
                (err: any) => {
                    this.subjectTypes = [];
                }
            );
    }

    public refreshNextUp(): void {
        this.loadingNextUp = true;
        this.nextUp = [];
        this.courseService.getNextUp()
            .subscribe((nextContent: Content[]) => {
                if (!nextContent.length) {
                    this.loadingNextUp = false;
                }
                nextContent.forEach((c: Content) => {
                    const course = this.ownedCourses.find((eachCourse: Course) => eachCourse.id === c.course);
                    if (course) {
                        this.courseService.getCourseContent(course.id)
                            .subscribe((courseContent: Content[]) => {
                                this.nextUp.push({ content: c, contentList: courseContent, course });
                                this.loadingNextUp = false;
                            });
                    }
                });
            });
    }

    public showHints(): void {
        this.intro.showHints();
    }

    public startIntro(): void {
        this.intro.start();
    }
}